import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import type { LoomVideo } from '@loomhq/record-sdk';

import { TOOLBAR_BUTTON } from '@atlaskit/editor-common/ui-menu';

import { EditorToolbarLoomButtonSpotlight } from '@confluence/loom-onboarding';
import type { OpenLoomRecorderParams } from '@confluence/loom-utils';
import {
	DiscoverabilityPulse,
	LoomCrossFlowPopupCard,
	LoomCrossJoinPopupCard,
	LoomRecorderLoadingBlanket,
	LoomRecorderTriggerEvent,
	useLoomEntryPointErrorCallback,
	useLoomEntryPointVariant,
	useLoomRecorderTriggerListener,
} from '@confluence/loom-utils';
import type { EditorLoomButtonProps } from '@confluence/loom-editor-utils';
import { withFlags } from '@confluence/flags';
import { useLoomInsertUrlState } from '@confluence/page-header-loom-button';

const ENTRYPOINT_ID = 'loom-confluence-page-editor-toolbar';

const i18n = defineMessages({
	crossSellPopupCardDescription: {
		id: 'editor-presets.full-page-editor.crossSellPopupCardDescription',
		defaultMessage: 'Save time and engage your team with a video walkthrough of this page.',
		description: 'Description inside a popup card advertising an opportunity to try Loom',
	},
	crossSellPopupCardTitle: {
		id: 'editor-presets.full-page-editor.crossSellPopupCardTitle',
		defaultMessage: 'Share your work with a video',
		description: 'Title of a popup card advertising an opportunity to try Loom',
	},
});

const EditorLoomButtonComponent = ({
	buttonComponent: ButtonComponent,
	editorAPIRef,
	flags,
}: EditorLoomButtonProps) => {
	const intl = useIntl();
	const { entryPointVariant, error, loading } = useLoomEntryPointVariant();

	const loomEntryPointErrorCallback = useLoomEntryPointErrorCallback({ flags });

	const [, loomInsertUrlActions] = useLoomInsertUrlState();

	const openLoomRecorderParams: OpenLoomRecorderParams = {
		onInsert: (_, video: LoomVideo) => {
			/**
			 * It DOES NOT actually do any insertion, but it triggers the 'useFullPageEditorLoomInsertion' hook in the 'FullPageEditorComponent' to do the actual insertion.
			 */
			loomInsertUrlActions.updateFullPageEditorLoomUrl(video);
		},
		/**
		 * intentionally not localized because Loom RecordSDK does not support i18n
		 * the orginal config(insertButtonText) is defined in "confluence/next/packages/fabric-providers/src/getLoomProvider.ts"
		 */
		insertButtonText: 'Insert Loom video',
	};

	useLoomRecorderTriggerListener(
		{
			event: LoomRecorderTriggerEvent.CROSS_FLOW_SUCCESS,
			entryPointId: ENTRYPOINT_ID,
		},
		openLoomRecorderParams,
		{
			entryPointLocation: 'editor-toolbar', // to match existing behavior (from the config defined in useFullPageEditorPreset.ts)
			flags,
			resetInsertFunctionOnUnmount: true, // to be consistent with the config in 'useFullPageEditorPreset.ts', i.e. if the user navigates to a different page, the 'Insert Loom video' button is replaced with the 'Open on Loom' button
		},
	);

	if (entryPointVariant === 'BETA') {
		// Fall back to the plugin's default behavior when in a recorder-eligible state
		return <ButtonComponent />;
	}

	if (error) {
		return <ButtonComponent onClickBeforeInit={loomEntryPointErrorCallback} />;
	}

	if (loading) {
		return <ButtonComponent isDisabled />;
	}

	if (entryPointVariant === 'CROSS_FLOW') {
		return (
			<>
				<LoomCrossFlowPopupCard
					description={intl.formatMessage(i18n.crossSellPopupCardDescription)}
					entryPointId={ENTRYPOINT_ID}
					placement="bottom-end"
					// Render in a Portal to isolate from conflicts caused by editor-based CSS + keyboard & focus behavior
					shouldRenderToParent={false}
					sourceActionSubjectId={TOOLBAR_BUTTON.RECORD_VIDEO}
					title={intl.formatMessage(i18n.crossSellPopupCardTitle)}
					trigger={({ isSelected, onClick, ...otherTriggerProps }) => {
						return (
							<DiscoverabilityPulse entryPointId={ENTRYPOINT_ID}>
								<ButtonComponent
									onClickBeforeInit={onClick}
									selected={isSelected}
									{...otherTriggerProps}
								/>
							</DiscoverabilityPulse>
						);
					}}
				/>
				{/* LoomRecorderLoadingBlanket is shown when admin returns from CFFE, it needs ~1 second to poll license-information to confirm if Loom is provisioned */}
				<LoomRecorderLoadingBlanket entryPointId={ENTRYPOINT_ID} />
			</>
		);
	}

	if (entryPointVariant === 'CROSS_JOIN') {
		return (
			<LoomCrossJoinPopupCard
				description={intl.formatMessage(i18n.crossSellPopupCardDescription)}
				entryPointId={ENTRYPOINT_ID}
				placement="bottom-end"
				// Render in a Portal to isolate from conflicts caused by editor-based CSS + keyboard & focus behavior
				shouldRenderToParent={false}
				sourceActionSubjectId={TOOLBAR_BUTTON.RECORD_VIDEO}
				title={intl.formatMessage(i18n.crossSellPopupCardTitle)}
				triggerLink={(href, { onClick, ...otherTriggerProps }) => (
					<DiscoverabilityPulse entryPointId={ENTRYPOINT_ID}>
						<ButtonComponent
							{...otherTriggerProps}
							href={href}
							onClickBeforeInit={onClick}
							rel="noreferrer"
							target="_blank"
						/>
					</DiscoverabilityPulse>
				)}
			/>
		);
	}

	if (entryPointVariant === 'CO_USE') {
		return (
			<>
				<EditorToolbarLoomButtonSpotlight
					editorAPIRef={editorAPIRef}
					spotlightPlacement="bottom right"
					nudgeSource="fullPageEditorToolbar"
					epMessage="cc-loom-onboarding-spotlight-editor"
				>
					<ButtonComponent />
				</EditorToolbarLoomButtonSpotlight>
				{/* LoomRecorderLoadingBlanket is shown when admin return from CFFE and confirm Loom is provisioned, we keep it showing until it is in the 'pre-recording' sdk state (took 2~8 seconds roughly) */}
				<LoomRecorderLoadingBlanket entryPointId={ENTRYPOINT_ID} />
			</>
		);
	}

	return null;
};

export const EditorLoomButton = withFlags(EditorLoomButtonComponent);
